import type { PersonDto, RoleDto } from '@ukinfo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useScope } from '../lib/scope';
import { PageHeader } from '../components/PageHeader';
import { PersonAccessCard } from '../components/PersonAccessCard';
import { RoleActions, roleValidity } from '../components/PersonRoles';
import { PersonPermissionsCard } from '../components/PermissionsAdmin';
import { formatDate } from '../lib/format';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Dialog,
  ErrorBlock,
  Field,
  Input,
  LoadingBlock,
  Select,
  useToast,
} from '../components/ui';

interface Membership {
  id: string;
  organisationUnitId: string;
  status: string;
  entryDate: string;
  exitDate: string | null;
  birthDate: string | null;
  address: string;
  emergencyContact: string;
  annualContributionCents?: number;
  archived: boolean;
  canUpdate: boolean;
}

type PersonDetail = PersonDto & { memberships: Membership[] };

interface Unit {
  id: string;
  name: string;
  path: string[];
}

const STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Aktiv',
  PASSIVE: 'Passiv',
  EXITED: 'Ausgetreten',
};

const heute = (): string => new Date().toISOString().slice(0, 10);
const euro = (cents?: number): string =>
  cents === undefined
    ? '—'
    : new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR' }).format(cents / 100);

/**
 * Alles zu einer Person an einer Stelle.
 *
 * Vorher war dieselbe Person an drei Orten zu pflegen: Stammdaten und
 * Funktion unter *Verein*, die Mitgliedschaft unter *Mitglieder* - dort noch
 * einmal mit Bereichsauswahl. Diese Seite fuehrt beides zusammen; die
 * Mitgliederliste bleibt die gefilterte Sicht darauf.
 */
export function PersonPage(): JSX.Element {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const { can } = useAuth();
  const scope = useScope();

  const person = useQuery({
    queryKey: ['person', id],
    queryFn: () => api.get<PersonDetail>(`/api/persons/${id}`),
    enabled: Boolean(id),
  });
  const roles = useQuery({
    queryKey: ['roles'],
    queryFn: () => api.get<RoleDto[]>('/api/roles'),
    select: (list) => list.filter((role) => !role.archived),
  });
  const units = useQuery({
    queryKey: ['organisation', 'units'],
    queryFn: () => api.get<Unit[]>('/api/organisation/units'),
  });

  const [stammdaten, setStammdaten] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [personBirthDate, setPersonBirthDate] = useState('');
  const [playerNumber, setPlayerNumber] = useState('');

  const [funktion, setFunktion] = useState(false);
  const [roleId, setRoleId] = useState('');
  const [roleUnitId, setRoleUnitId] = useState('');

  const [mitglied, setMitglied] = useState<Membership | 'neu' | null>(null);
  const [unitId, setUnitId] = useState('');
  const [entryDate, setEntryDate] = useState(heute());
  const [exitDate, setExitDate] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [status, setStatus] = useState('ACTIVE');
  const [address, setAddress] = useState('');
  const [emergencyContact, setEmergencyContact] = useState('');
  const [contribution, setContribution] = useState('');

  // Wird der Dialog geoeffnet, bevor die Bereichsliste da ist, bleibt die
  // Auswahl leer und "Speichern" dauerhaft gesperrt - ohne dass erkennbar
  // waere, warum. Deshalb nachtragen, sobald die Liste eintrifft.
  useEffect(() => {
    if (mitglied === 'neu' && !unitId && units.data?.length) {
      setUnitId(scope.unitId ?? units.data[0]!.id);
    }
  }, [mitglied, unitId, units.data, scope.unitId]);

  const fehler = (fallback: string) => (error: unknown) =>
    toast.show(error instanceof ApiError ? error.message : fallback, 'error');
  const neuLaden = async (): Promise<void> => {
    await qc.invalidateQueries({ queryKey: ['person', id] });
    await qc.invalidateQueries({ queryKey: ['persons'] });
    await qc.invalidateQueries({ queryKey: ['members'] });
  };

  const speichereStammdaten = useMutation({
    mutationFn: () =>
      api.patch(`/api/persons/${id}`, {
        firstName,
        lastName,
        email: email || null,
        phone: phone || null,
        birthDate: personBirthDate || null,
        playerNumber: playerNumber.trim() || null,
      }),
    onSuccess: async () => {
      await neuLaden();
      setStammdaten(false);
      toast.show('Person gespeichert');
    },
    onError: fehler('Speichern fehlgeschlagen'),
  });

  const speichereFunktion = useMutation({
    mutationFn: () =>
      api.post('/api/person-roles', {
        personId: id,
        roleId,
        organisationUnitId: roleUnitId,
        inheritsToChildren: true,
      }),
    onSuccess: async () => {
      await neuLaden();
      setFunktion(false);
      toast.show('Funktion zugewiesen');
    },
    onError: fehler('Funktion konnte nicht zugewiesen werden'),
  });

  const speichereMitgliedschaft = useMutation({
    mutationFn: () => {
      const daten = {
        entryDate,
        exitDate: exitDate || null,
        birthDate: birthDate || null,
        status,
        address,
        emergencyContact,
        annualContributionCents: contribution
          ? Math.round(Number(contribution) * 100)
          : undefined,
      };
      return mitglied === 'neu'
        ? api.post('/api/members', { personId: id, organisationUnitId: unitId, ...daten })
        : api.patch(`/api/members/${(mitglied as Membership).id}`, daten);
    },
    onSuccess: async () => {
      await neuLaden();
      setMitglied(null);
      toast.show('Mitgliedschaft gespeichert');
    },
    onError: fehler('Mitgliedschaft konnte nicht gespeichert werden'),
  });

  if (person.isLoading) return <LoadingBlock />;
  if (person.error) return <ErrorBlock error={person.error} />;
  if (!person.data) return <ErrorBlock error={new Error('Person nicht gefunden')} />;

  const p = person.data;
  const darfPersonen = can('persons', 'update');
  // Wie am Server: Struktur bearbeiten UND Rechte verwalten.
  const darfFunktionen = can('organisation', 'update') && can('permissions', 'manage');

  const oeffneStammdaten = (): void => {
    setFirstName(p.firstName);
    setLastName(p.lastName);
    setEmail(p.email ?? '');
    setPhone(p.phone ?? '');
    setPersonBirthDate(p.birthDate ?? '');
    setPlayerNumber(p.playerNumber ?? '');
    setStammdaten(true);
  };

  const oeffneMitgliedschaft = (vorhanden: Membership | null): void => {
    setMitglied(vorhanden ?? 'neu');
    setUnitId(vorhanden?.organisationUnitId ?? scope.unitId ?? units.data?.[0]?.id ?? '');
    setEntryDate(vorhanden?.entryDate ?? heute());
    setExitDate(vorhanden?.exitDate ?? '');
    setBirthDate(vorhanden?.birthDate ?? p.birthDate ?? '');
    setStatus(vorhanden?.status ?? 'ACTIVE');
    setAddress(vorhanden?.address ?? '');
    setEmergencyContact(vorhanden?.emergencyContact ?? '');
    setContribution(
      vorhanden?.annualContributionCents === undefined
        ? ''
        : String(vorhanden.annualContributionCents / 100),
    );
  };

  const bereichsName = (unitIdToFind: string): string =>
    units.data?.find((u) => u.id === unitIdToFind)?.name ?? 'Bereich';

  return (
    <div className="space-y-4">
      <PageHeader
        title={p.displayName}
        description={[p.email, p.phone, p.birthDate ? `geb. ${p.birthDate.split('-').reverse().join('.')}` : null, p.playerNumber ? `Spielernr. ${p.playerNumber}` : null].filter(Boolean).join(' · ') || 'Keine Kontaktdaten'}
        scoped={false}
        actions={
          <>
            <Button variant="secondary" onClick={() => navigate(-1)}>
              Zurück
            </Button>
            {darfPersonen && <Button onClick={oeffneStammdaten}>Stammdaten bearbeiten</Button>}
          </>
        }
      />

      {!p.active && <Badge tone="neutral">Inaktiv</Badge>}

      <Card>
        <CardBody className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-semibold">Funktionen</h2>
            {darfFunktionen && (
              <Button
                size="sm"
                onClick={() => {
                  setRoleId(roles.data?.[0]?.id ?? '');
                  setRoleUnitId(scope.unitId ?? units.data?.[0]?.id ?? '');
                  setFunktion(true);
                }}
              >
                Funktion hinzufügen
              </Button>
            )}
          </div>
          {p.roles.length === 0 ? (
            <p className="text-sm text-slate-500">
              Noch keine Funktion. Über die Funktion gehört eine Person zu einem Bereich.
            </p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {p.roles.map((role) => (
                <li key={role.id} className="flex items-center justify-between gap-2 py-2 text-sm">
                  <span>
                    <Badge tone={role.isCurrent ? 'brand' : 'neutral'}>{role.roleName}</Badge>
                    <span className="ml-2 text-slate-600">{role.organisationUnitName}{roleValidity(role)}</span>
                  </span>
                  <RoleActions role={role} />
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <PersonAccessCard personId={p.id} personName={p.displayName} personEmail={p.email} personActive={p.active} />

      <PersonPermissionsCard personId={p.id} />

      <Card>
        <CardBody className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-semibold">Mitgliedschaft</h2>
            {darfPersonen && (
              <Button size="sm" variant="secondary" onClick={() => oeffneMitgliedschaft(null)}>
                Mitgliedschaft anlegen
              </Button>
            )}
          </div>
          {p.memberships.length === 0 ? (
            <p className="text-sm text-slate-500">
              Keine Mitgliedschaft erfasst. Beiträge und Eintrittsdatum gehören hierher, die
              Zugehörigkeit zu einem Bereich zur Funktion.
            </p>
          ) : (
            <ul className="space-y-2">
              {p.memberships.map((m) => (
                <li key={m.id} className="rounded-lg bg-slate-50 px-3 py-2">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <span>
                      <p className="font-medium">{bereichsName(m.organisationUnitId)}</p>
                      <p className="text-xs text-slate-500">
                        Eintritt {formatDate(m.entryDate)}
                        {m.exitDate ? ` · Austritt ${formatDate(m.exitDate)}` : ''} ·{' '}
                        {euro(m.annualContributionCents)}
                      </p>
                    </span>
                    <span className="flex items-center gap-2">
                      <Badge tone={m.archived ? 'neutral' : m.status === 'ACTIVE' ? 'success' : 'warning'}>
                        {m.archived ? 'Archiviert' : (STATUS_LABELS[m.status] ?? m.status)}
                      </Badge>
                      {m.canUpdate && (
                        <Button size="sm" variant="ghost" onClick={() => oeffneMitgliedschaft(m)}>
                          Bearbeiten
                        </Button>
                      )}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <Dialog
        open={stammdaten}
        onClose={() => setStammdaten(false)}
        title="Stammdaten bearbeiten"
        footer={
          <>
            <Button variant="secondary" onClick={() => setStammdaten(false)}>
              Abbrechen
            </Button>
            <Button
              loading={speichereStammdaten.isPending}
              disabled={!firstName || !lastName}
              onClick={() => speichereStammdaten.mutate()}
            >
              Speichern
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Vorname" required>
              {(fid) => (
                <Input id={fid} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              )}
            </Field>
            <Field label="Nachname" required>
              {(fid) => (
                <Input id={fid} value={lastName} onChange={(e) => setLastName(e.target.value)} />
              )}
            </Field>
          </div>
          <Field label="E-Mail">
            {(fid) => (
              <Input id={fid} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            )}
          </Field>
          <Field label="Telefon">
            {(fid) => <Input id={fid} value={phone} onChange={(e) => setPhone(e.target.value)} />}
          </Field>
          <Field label="Geburtsdatum" hint="Gilt auch für Mitgliedschaft und Jahrgangsplanung">
            {(fid) => <Input id={fid} type="date" max={new Date().toISOString().slice(0, 10)} value={personBirthDate} onChange={(e) => setPersonBirthDate(e.target.value)} />}
          </Field>
          <Field label="Spielernummer (Verband)" hint="Nummer des Spielerpasses, nicht die Rückennummer">
            {(fid) => <Input id={fid} inputMode="numeric" maxLength={12} value={playerNumber} onChange={(e) => setPlayerNumber(e.target.value.replace(/\D/g, ''))} />}
          </Field>
        </div>
      </Dialog>

      <Dialog
        open={funktion}
        onClose={() => setFunktion(false)}
        title="Funktion hinzufügen"
        footer={
          <>
            <Button variant="secondary" onClick={() => setFunktion(false)}>
              Abbrechen
            </Button>
            <Button
              loading={speichereFunktion.isPending}
              disabled={!roleId || !roleUnitId}
              onClick={() => speichereFunktion.mutate()}
            >
              Zuweisen
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Funktion" required>
            {(fid) => (
              <Select id={fid} value={roleId} onChange={(e) => setRoleId(e.target.value)}>
                {roles.data?.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Bereich" required>
            {(fid) => (
              <Select id={fid} value={roleUnitId} onChange={(e) => setRoleUnitId(e.target.value)}>
                {(units.data ?? []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.path.join(' / ') || u.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
      </Dialog>

      <Dialog
        open={mitglied !== null}
        onClose={() => setMitglied(null)}
        title={mitglied === 'neu' ? 'Mitgliedschaft anlegen' : 'Mitgliedschaft bearbeiten'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setMitglied(null)}>
              Abbrechen
            </Button>
            <Button
              loading={speichereMitgliedschaft.isPending}
              disabled={(mitglied === 'neu' && !unitId) || !entryDate}
              onClick={() => speichereMitgliedschaft.mutate()}
            >
              Speichern
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {mitglied === 'neu' && (
            <Field label="Bereich" required>
              {(fid) => (
                <Select id={fid} value={unitId} onChange={(e) => setUnitId(e.target.value)}>
                  {(units.data ?? []).map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.path.join(' / ') || u.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Eintritt" required>
              {(fid) => (
                <Input
                  id={fid}
                  type="date"
                  value={entryDate}
                  onChange={(e) => setEntryDate(e.target.value)}
                />
              )}
            </Field>
            <Field label="Austritt">
              {(fid) => (
                <Input
                  id={fid}
                  type="date"
                  value={exitDate}
                  onChange={(e) => setExitDate(e.target.value)}
                />
              )}
            </Field>
            <Field label="Geburtsdatum">
              {(fid) => (
                <Input
                  id={fid}
                  type="date"
                  value={birthDate}
                  onChange={(e) => setBirthDate(e.target.value)}
                />
              )}
            </Field>
            <Field label="Status">
              {(fid) => (
                <Select id={fid} value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="ACTIVE">Aktiv</option>
                  <option value="PASSIVE">Passiv</option>
                  <option value="EXITED">Ausgetreten</option>
                </Select>
              )}
            </Field>
          </div>
          <Field label="Jahresbeitrag in Euro">
            {(fid) => (
              <Input
                id={fid}
                type="number"
                min="0"
                step="0.01"
                value={contribution}
                onChange={(e) => setContribution(e.target.value)}
              />
            )}
          </Field>
          <Field label="Adresse">
            {(fid) => (
              <Input id={fid} value={address} onChange={(e) => setAddress(e.target.value)} />
            )}
          </Field>
          <Field label="Notfallkontakt">
            {(fid) => (
              <Input
                id={fid}
                value={emergencyContact}
                onChange={(e) => setEmergencyContact(e.target.value)}
              />
            )}
          </Field>
        </div>
      </Dialog>
    </div>
  );
}
